<script setup>
import {computed, onMounted, reactive, ref, watch} from "vue";
defineOptions({ name: 'TicketsCompletedView' })

import {useTicketStore} from "../../store/tickets.js";
import {useAuthStore} from "../../store/auth.js";
import EmptyState from "../../components/global/EmptyState.vue";
import SectionHeader from "../../components/section/SectionHeader.vue";
import Pagination from "../../components/section/Pagination.vue";
import TicketRowItem from "../../components/tickets/TicketRowItem.vue";
import {useModalStore} from "../../store/modal.js";

const modalStore = useModalStore()
const store = useTicketStore()
const authStore = useAuthStore()
const scrollSection = ref()

const isAdmin = computed(() =>
    [
        'reseller-super-admin',
        'reseller-team-leader',
        'super-admin',
        'internal-super-admin',
        'internal-team-leader'
    ].includes(authStore.user.roles[0].name)
)

const MIN_SEARCH_LENGTH = 3

let searchDebounce
const search = ref('')

const activeSearch = computed(() => {
    const query = search.value?.trim() ?? ''

    return query.length >= MIN_SEARCH_LENGTH ? query : null
})

const filter_data = ref()

const ticketParams = reactive({
    sort_direction: 'desc',
    assignee_id: isAdmin.value ? null : authStore.user.id,
    status: null,
    assignment: null,
    linkage: null,
    limit: 25,
    start_date: null,
    end_date: null,
    complete: 1
})

const clearDateFilters = () => {
    ticketParams.start_date = null
    ticketParams.end_date = null
}

const clearOtherFilters = () => {
    ticketParams.sort_direction = 'desc'
    ticketParams.assignee_id = isAdmin.value ? null : authStore.user.id
    ticketParams.status = null
    ticketParams.assignment = null
    ticketParams.linkage = null
    ticketParams.start_date = null
    ticketParams.end_date = null
    ticketParams.complete = 1
}

watch(search, (value, oldValue) => {
    clearTimeout(searchDebounce)

    const query = value?.trim() ?? ''
    const previousQuery = oldValue?.trim() ?? ''

    if (query.length < MIN_SEARCH_LENGTH && previousQuery.length < MIN_SEARCH_LENGTH) {
        return
    }

    searchDebounce = setTimeout(() => getTickets(1), 250)
})

const getTickets = async (page = 1) => {
    await store.getTickets({
        ...ticketParams,
        search: activeSearch.value,
        page
    })

    if (scrollSection.value) {
        scrollSection.value.scrollTop = 0
    }
}

onMounted(async () => {
    await getTickets()

    await store.prepareFilterData()
    filter_data.value = store.filter_data
})
</script>

<template>
    <main v-if="!store.tickets">
        <empty-state
            icon="call-voice"
            heading="Loading tickets."
            content="Give it sec, it's coming in a mo."
        />
    </main>

    <main v-else-if="store.tickets.data.length || search">
        <section>
            <section-header
                :heading="`${isAdmin ? 'All completed tickets.' : 'Your completed tickets.'}`"
            >
                <div class="search-bar">
                    <brand-form-input
                        label="Search by Kayako number, order UUID or Afrihost ref"
                        variant="search"
                        v-model="search"
                    />

                    <button
                        class="mobile-filter-button"
                        @click.prevent="modalStore.open('TicketFilters', {}, 'body', 'bottom-right')"
                    >
                        <ah-icon name="filter-default" />
                    </button>
                </div>
            </section-header>

            <div
                class="section-table"
                ref="scrollSection"
                v-if="store.tickets.data.length"
            >
                <ticket-row-item
                    :ticket
                    completed
                    v-for="ticket in store.tickets.data"
                    :key="ticket.id"
                />
            </div>

            <div
                class="section-table-empty"
                v-if="!store.tickets.data.length"
            >
                <h1>No tickets match</h1>
            </div>

            <div
                class="section-footer"
                v-if="store.tickets.data.length"
            >
                <pagination
                    :pagination="store.tickets"
                    @change-page="getTickets"
                />
            </div>
        </section>

        <aside class="section-sidebar">
            <div class="sidebar-header">
                <h2>Filters</h2>
                <p @click.prevent="clearOtherFilters">Clear</p>
            </div>

            <div class="sidebar-select">
                <brand-form-select
                    variant="sidebar"
                    label="Sorting."
                    :options="filter_data?.sorting ?? []"
                    v-model="ticketParams.sort_direction"
                />
            </div>

            <div
                class="sidebar-select"
                v-if="isAdmin"
            >
                <brand-form-select
                    variant="sidebar"
                    label="Assignee."
                    :options="filter_data?.assignees ?? []"
                    v-model="ticketParams.assignee_id"
                />
            </div>

            <div class="sidebar-select">
                <brand-form-select
                    variant="sidebar"
                    label="Status."
                    :options="filter_data?.statuses ?? []"
                    v-model="ticketParams.status"
                />
            </div>

            <div class="sidebar-select">
                <brand-form-select
                    variant="sidebar"
                    label="Assignment."
                    :options="filter_data?.assignment ?? []"
                    v-model="ticketParams.assignment"
                />
            </div>

            <div class="sidebar-select">
                <brand-form-select
                    variant="sidebar"
                    label="Linkage."
                    :options="filter_data?.linkage ?? []"
                    v-model="ticketParams.linkage"
                />
            </div>

            <div class="sidebar-header">
                <h2>Date</h2>
                <p @click.prevent="clearDateFilters">Clear</p>
            </div>

            <brand-form-input
                label="Start date."
                variant="date"
                v-model="ticketParams.start_date"
            />

            <brand-form-input
                label="End date."
                variant="date"
                v-model="ticketParams.end_date"
            />

            <div class="sidebar-footer">
                <brand-button
                    as="button"
                    variant="primary"
                    @click.prevent="getTickets(1)"
                >
                    Apply
                </brand-button>

                <brand-button
                    as="button"
                    variant="small"
                >
                    <ah-icon name="arrow-in" />
                </brand-button>
            </div>
        </aside>
    </main>

    <main v-else>
        <empty-state
            icon="cart-default"
            heading="No completed tickets."
            content="Once tickets are completed they will show up here."
        />
    </main>
</template>

<style scoped lang="scss">
.search-bar {
    min-width: 34rem;
}
</style>
